import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth, useTenantsState } from '@frontegg/react';
import { useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { SidebarProvider, SidebarInset } from '../components/ui/sidebar';
import AuthService from '../services/auth.service';
import FeatureFlagService from '../services/featureFlag.service';
import { FeatureFlag, useFeatureFlag } from '../services/featureFlag.service';
import { useWhitelabelOverrides } from '../hooks/useWhitelabelOverrides';
import { UnsavedChangesProvider } from '../contexts/UnsavedChangesContext';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { ApiError } from '../services/api.service';

export default function AuthenticatedRootPage() {
  const { isAuthenticated: fronteggAuthenticated, isLoading, user } = useAuth();
  const { tenants } = useTenantsState();
  const location = useLocation();
  useWhitelabelOverrides();
  const { error: configError, isError: isConfigError } = useTenantConfig();

  // Check for system session auth (used by screengrabber/notifications)
  const hasSystemSession = !fronteggAuthenticated && !!AuthService.getInstance().getAuthState();
  const isAuthenticated = fronteggAuthenticated || hasSystemSession;

  // Sync Frontegg auth state with AuthService
  useEffect(() => {
    if (user && isAuthenticated) {
      // Create auth state from Frontegg
      // Map IRole[] to string[] for roles
      const roleNames = (user.roles || []).map(role =>
        typeof role === 'string' ? role : role.name || role.key || ''
      );
      console.log('[AuthenticatedRootPage] User roles (raw):', JSON.stringify(user.roles, null, 2));
      console.log('[AuthenticatedRootPage] User roles (mapped):', roleNames);
      const authState = {
        sub: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant_id: user.tenantId || '',
        roles: roleNames,
        tenant_name: user.tenantId || 'Tenant',
        access_token: user.accessToken || '',
        profile_img_url: user.profilePictureUrl || undefined,
        tenants: [],
        customClaims: (user as any).customClaims || {},
      };
      AuthService.getInstance().setAuthState(authState);
      const currentTenant = tenants?.find((t: any) => t.tenantId === user.tenantId);
      FeatureFlagService.getInstance().identify({
        key: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant: currentTenant?.name || 'Tenant',
      });
    }
  }, [user, isAuthenticated]);

  // After SSO callback, check if we need to redirect back to a custom domain
  useEffect(() => {
    if (!isAuthenticated) return;
    const returnTo = localStorage.getItem('externalReturnTo');
    const returnToken = localStorage.getItem('externalReturnToken');
    if (returnTo && returnToken) {
      localStorage.removeItem('externalReturnTo');
      localStorage.removeItem('externalReturnToken');
      window.location.href = `/api/v1/env/return?url=${encodeURIComponent(returnTo)}&token=${encodeURIComponent(returnToken)}`;
    }
  }, [isAuthenticated]);

  // Redirect authenticated users to legacy frontend when force-legacy flag is enabled
  const forceLegacy = useFeatureFlag(FeatureFlag.FORCE_LEGACY);
  useEffect(() => {
    const authService = AuthService.getInstance();
    if (forceLegacy && authService.legacyAppUrl && isAuthenticated) {
      window.location.href = authService.legacyAppUrl;
    }
  }, [forceLegacy, isAuthenticated]);

  // Store return path for redirect after login
  useEffect(() => {
    if (location && !location.pathname.includes('/login') && !location.pathname.includes('/logout')) {
      const existingReturnPath = localStorage.getItem('returnPath');
      if (!existingReturnPath && location.hash !== '#returning') {
        localStorage.setItem('returnPath', location.pathname + location.search);
      }
    }
  }, [location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const authService = AuthService.getInstance();
    if (authService.useExternalLogin) {
      // Locally, Frontegg OAuth is configured for a different port, so redirect there for login
      window.location.href = `${authService.authAppUrl}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }

  if (isConfigError && configError instanceof ApiError && configError.code === 404) {
    const payload = configError.payload as any;
    if (payload?.archived) {
      return <Navigate to="/tenant-archived" state={{ tenantName: payload.tenant_name }} replace />;
    }
    return <Navigate to="/tenant-not-found" replace />;
  }

  return (
    <SidebarProvider>
      <Sidebar />
      <SidebarInset>
        <UnsavedChangesProvider>
          <Outlet />
        </UnsavedChangesProvider>
      </SidebarInset>
    </SidebarProvider>
  );
}

